/**
 * PrintTemplateMenu — Dinamik Yazdırma Şablon Havuzu
 * Bir sayfaya atanmış aktif şablonları çeker ve yazdırma menüsü gösterir.
 * Şablonlardaki {{değişken}} yer tutucularını gerçek verilerle doldurur.
 */
import React, { useState, useEffect, useRef } from "react";
import { Printer, ChevronDown, FileText, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { printViaIframe } from "../utils/pdfUtils";
import { fmt, fmtDate } from "../appUtils";

// ── Sayfa tanımları (şablona atanabilir sayfalar) ─────────────────────────────
export const PRINT_TEMPLATE_PAGES = [
  { id: "orders",    label: "Siparişler" },
  { id: "customers", label: "Cari / Müşteriler" },
  { id: "stock",     label: "Stok" },
  { id: "quotes",    label: "Teklifler" },
  { id: "invoice",   label: "Faturalar" },
];

// ── Veri Şemaları — her sayfa için değişken tanımları ──────────────────────────
export const DATA_SCHEMAS = {
  customers: {
    label: "Müşteri (Cari)",
    vars: [
      { key: "name",     label: "Müşteri Adı",  example: "Ahmet Yılmaz" },
      { key: "code",     label: "Cari Kodu",    example: "C-001" },
      { key: "balance",  label: "Bakiye",       example: "1.250,00 TL" },
      { key: "phone",    label: "Telefon",      example: "+90 555 000 00 00" },
      { key: "email",    label: "E-posta",      example: "musteri@email.com" },
      { key: "city",     label: "Şehir",        example: "İstanbul" },
      { key: "type",     label: "Cari Tipi",    example: "Yurt İçi Müşteri" },
      { key: "currency", label: "Para Birimi",  example: "TL" },
      { key: "taxNo",    label: "Vergi No",     example: "1234567890" },
      { key: "address",  label: "Adres",        example: "Atatürk Cad. No:1" },
    ],
  },
  orders: {
    label: "Sipariş",
    vars: [
      { key: "orderNo",      label: "Sipariş No",      example: "SP-2026-0042" },
      { key: "customerName", label: "Müşteri Adı",     example: "Ahmet Yılmaz" },
      { key: "total",        label: "Toplam Tutar",    example: "8.500,00 TL" },
      { key: "status",       label: "Durum",           example: "Üretimde" },
      { key: "date",         label: "Sipariş Tarihi",  example: "02.09.2026" },
      { key: "city",         label: "Şehir",           example: "İstanbul" },
      { key: "currency",     label: "Para Birimi",     example: "TL" },
      { key: "note",         label: "Not",             example: "Kırılabilir" },
    ],
  },
  stock: {
    label: "Stok Ürün",
    vars: [
      { key: "name",     label: "Ürün Adı",   example: "Stor Perde Kumaş" },
      { key: "sku",      label: "SKU / Kod",  example: "STK-0042" },
      { key: "qty",      label: "Stok",       example: "25" },
      { key: "unit",     label: "Birim",      example: "mt" },
      { key: "price",    label: "Fiyat",      example: "85,00 TL" },
    ],
  },
};

// ── Veri çözücü — {{key}} → gerçek değer ─────────────────────────────────────
function resolveVar(key, data, schema, rates) {
  if (!data) return "";
  const s = DATA_SCHEMAS[schema];
  if (!s) return data[key] != null ? String(data[key]) : "";
  switch (schema) {
    case "customers":
      if (key === "balance") return `${fmt(data.balance || 0, data.currency || "TL")} ${data.currency || "TL"}`;
      if (key === "city")    return (data.address || "").split(/[\/,]/)[0] || "";
      if (key === "code")    return data.code || data.id || "";
      return data[key] != null ? String(data[key]) : "";
    case "orders":
      if (key === "total")   return `${fmt(data.total || 0, data.currency || "TL")} ${data.currency || "TL"}`;
      if (key === "date")    return fmtDate(data.createdAt || data.approvedAt || "");
      if (key === "orderNo") return data.orderNo || data.id || "";
      return data[key] != null ? String(data[key]) : "";
    default:
      return data[key] != null ? String(data[key]) : "";
  }
}

// ── Template HTML renderer ────────────────────────────────────────────────────
function renderTemplateHtml(template, data) {
  const schema = template.dataSchema || "orders";
  const size = typeof template.size === "object" ? template.size : { w: 210, h: 297 };
  const w = size.w || 210;
  const h = size.h || 297;

  const elements = (template.elements || []).map((el) => {
    const x = (el.x_mm || 0).toFixed(2);
    const y = (el.y_mm || 0).toFixed(2);

    // Resolve {{variable}} in text fields
    const rawText = el.text || "";
    const resolvedText = rawText.replace(/\{\{(\w+)\}\}/g, (_, key) => resolveVar(key, data, schema, {}));

    if (el.type === "hline") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm || w - 4).toFixed(2)}mm;height:${Math.max(0.3, (el.fontSize || 2) * 0.03).toFixed(2)}mm;background:#000;"></div>`;
    }
    if (el.type === "rect") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm || 40).toFixed(2)}mm;height:${(el.heightMm || 20).toFixed(2)}mm;border:${(el.borderThick || 0.5).toFixed(2)}mm solid #000;box-sizing:border-box;"></div>`;
    }
    if (el.type === "image_logo") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm || 30).toFixed(2)}mm;height:${(el.heightMm || 12).toFixed(2)}mm;display:flex;align-items:center;justify-content:center;font-size:7pt;color:#999;border:0.3mm solid #eee;">LOGO</div>`;
    }
    if (el.type === "barcode") {
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm || 50).toFixed(2)}mm;height:${(el.heightMm || 15).toFixed(2)}mm;font-family:monospace;font-size:7pt;border:0.2mm solid #ddd;display:flex;align-items:center;justify-content:center;">${resolvedText}</div>`;
    }
    const ptSize = ((el.fontSize || 26) * 0.354 * 0.125 * 3.78).toFixed(1);
    const rot = el.rotation ? "transform:rotate(90deg);transform-origin:top left;" : "";
    const bold = el.bold !== false ? "font-weight:700;" : "";
    return `<div style="position:absolute;left:${x}mm;top:${y}mm;font-size:${ptSize}pt;${bold}font-family:Arial,sans-serif;white-space:pre-wrap;${rot}">${String(resolvedText).replace(/</g, "&lt;")}</div>`;
  }).join("\n");

  return `<!DOCTYPE html>
<html lang="tr"><head>
<meta charset="utf-8">
<style>
  @page { size: ${w}mm ${h}mm; margin: 0; }
  *, html, body { box-sizing: border-box; margin: 0; padding: 0; }
  body { width: ${w}mm; height: ${h}mm; background: #fff; overflow: hidden; }
</style>
</head>
<body>
<div style="position:relative;width:${w}mm;height:${h}mm;overflow:hidden;">
${elements}
</div>
</body></html>`;
}

// ── Bileşen ───────────────────────────────────────────────────────────────────
export default function PrintTemplateMenu({ page, data, buttonLabel = "Şablon Yazdır", className = "" }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data: rows } = await api.get("/settings", { params: { category: "print_templates_v2" } });
        if (mounted) {
          const active = (rows || []).filter(r => {
            try {
              const tpl = JSON.parse(r.value || "{}");
              return Array.isArray(tpl.pages) && tpl.pages.includes(page) && tpl.active !== false;
            } catch { return false; }
          }).map(r => {
            try { return { id: r.id, ...JSON.parse(r.value) }; } catch { return null; }
          }).filter(Boolean);
          setTemplates(active);
        }
      } catch {
        // Şablon yoksa sessizce devam
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [page]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (templates.length === 0 && !loading) return null;

  const handlePrint = (template) => {
    setOpen(false);
    const html = renderTemplateHtml(template, data);
    printViaIframe(html, false);
    toast.success(`"${template.name}" şablonu yazdırılıyor…`);
  };

  return (
    <div className={`relative ${className}`} ref={menuRef} data-testid="print-template-menu">
      <button
        onClick={() => setOpen(o => !o)}
        className="zen-btn-ghost !text-xs flex items-center gap-1.5"
        data-testid="print-template-menu-trigger"
        disabled={loading}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
        {buttonLabel}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] zen-panel !p-1 shadow-2xl border border-white/10 zen-anim-up">
          {templates.map(tpl => (
            <button
              key={tpl.id}
              onClick={() => handlePrint(tpl)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-white/5 text-left text-sm transition-colors group"
              data-testid={`print-tpl-${tpl.id}`}
            >
              <FileText className="w-4 h-4 text-slate-400 group-hover:text-[#E5322A]" />
              <div>
                <div className="font-medium text-slate-200">{tpl.name || "Şablon"}</div>
                <div className="text-[10px] text-slate-500">{tpl.size?.w || "?"}×{tpl.size?.h || "?"}mm</div>
              </div>
            </button>
          ))}
          {templates.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">Bu sayfa için şablon tanımlanmamış</div>
          )}
        </div>
      )}
    </div>
  );
}
